import {
  foldDiacritics,
  keywordAlphabet,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * The Polybius square and its descendant, the prisoners' tap code.
 *
 * Polybius: a 5 × 5 grid with I and J sharing a cell (optionally mixed by a keyword),
 * each letter written as its row then its column — H is 23. Tap code: the fixed grid
 * with K folded into C (I and J keep their own cells), each letter tapped as a group for
 * the row and a group for the column — H is ".. ...".
 *
 * Written form, both kinds: letters are separated by a space (two spaces in tap code,
 * whose letters are two groups each), words by " / ", and line breaks are kept. Only
 * letters are encoded; digits and other letters are reported, punctuation is dropped.
 * Decoding reads digits — or tap groups — in pairs and ignores everything else except
 * the slash, so spacing never matters.
 */

export type PolybiusKind = "polybius" | "tap";

const SIZE = 5;
const POLYBIUS_ALPHABET = "ABCDEFGHIKLMNOPQRSTUVWXYZ";
const TAP_ALPHABET = "ABCDEFGHIJLMNOPQRSTUVWXYZ";
const TAP = ".";
const LINE_BREAK = /\r?\n/;
const SPACES = /\s+/;
const WORD_BREAK = /\//;
const DIGITS = /[0-9]/g;
const TAP_GROUPS = /[.·•*]+/g;
const DIGIT_OR_LETTER = /[\p{Nd}\p{L}]/u;

/** A capital letter as the grid knows it: J → I (Polybius) or K → C (tap code). */
export function polybiusLetter(letter: string, kind: PolybiusKind): string {
  if (kind === "tap") {
    return letter === "K" ? "C" : letter;
  }
  return letter === "J" ? "I" : letter;
}

/** The grid's 25 letters, row by row. The tap code grid is fixed; it takes no keyword. */
export function polybiusSquare(keyword: string, kind: PolybiusKind): string {
  if (kind === "tap") {
    return TAP_ALPHABET;
  }
  return keywordAlphabet(keyword, POLYBIUS_ALPHABET, (letter) =>
    polybiusLetter(letter, "polybius")
  );
}

/** 1-based row and column of a letter (any case), or null for a non-letter. */
export function polybiusCoordinates(
  char: string,
  square: string,
  kind: PolybiusKind
): { row: number; column: number } | null {
  const index = letterIndex(char);
  if (index < 0) {
    return null;
  }
  const cell = square.indexOf(polybiusLetter(char.toUpperCase(), kind));
  if (cell < 0) {
    return null;
  }
  return { row: Math.floor(cell / SIZE) + 1, column: (cell % SIZE) + 1 };
}

function writeLetter(row: number, column: number, kind: PolybiusKind): string {
  if (kind === "tap") {
    return `${TAP.repeat(row)} ${TAP.repeat(column)}`;
  }
  return `${row}${column}`;
}

export interface PolybiusEncodeResult {
  output: string;
  /** Digits and non-A–Z letters, which have no cell. */
  skipped: string[];
}

export function polybiusEncode(
  text: string,
  square: string,
  kind: PolybiusKind
): PolybiusEncodeResult {
  const skipped = new Set<string>();
  const letterGap = kind === "tap" ? "  " : " ";
  const encodeWord = (word: string) => {
    const letters: string[] = [];
    for (const char of word) {
      const at = polybiusCoordinates(char, square, kind);
      if (at) {
        letters.push(writeLetter(at.row, at.column, kind));
      } else if (DIGIT_OR_LETTER.test(char)) {
        skipped.add(char);
      }
    }
    return letters.join(letterGap);
  };
  const lines = foldDiacritics(text)
    .split(LINE_BREAK)
    .map((line) =>
      line
        .split(SPACES)
        .map(encodeWord)
        .filter((word) => word !== "")
        .join(" / ")
    );
  return { output: lines.join("\n").trim(), skipped: [...skipped] };
}

export interface PolybiusDecodeResult {
  output: string;
  /** Pairs that point outside the grid (a 0, a 6–9, or more than five taps). */
  invalid: string[];
  /** Words that ended with half a pair. */
  unpaired: number;
}

/** Read one word's pairs of numbers (1–5) into letters. */
function decodePairs(
  values: readonly number[],
  square: string,
  result: { invalid: Set<string>; unpaired: number },
  describe: (row: number, column: number) => string
): string {
  let word = "";
  for (let index = 0; index + 1 < values.length; index += 2) {
    const row = values[index];
    const column = values[index + 1];
    const inGrid = row >= 1 && row <= SIZE && column >= 1 && column <= SIZE;
    if (inGrid) {
      word += square.charAt((row - 1) * SIZE + column - 1);
    } else {
      word += "?";
      result.invalid.add(describe(row, column));
    }
  }
  if (values.length % 2 === 1) {
    result.unpaired += 1;
  }
  return word;
}

export function polybiusDecode(
  text: string,
  square: string,
  kind: PolybiusKind
): PolybiusDecodeResult {
  const result = { invalid: new Set<string>(), unpaired: 0 };
  const readWord = (word: string) => {
    if (kind === "tap") {
      const taps = (word.match(TAP_GROUPS) ?? []).map(
        (group) => [...group].length
      );
      return decodePairs(taps, square, result, (row, column) =>
        writeLetter(row, column, "tap")
      );
    }
    const digits = (word.match(DIGITS) ?? []).map(Number);
    return decodePairs(
      digits,
      square,
      result,
      (row, column) => `${row}${column}`
    );
  };
  const lines = text.split(LINE_BREAK).map((line) =>
    line
      .split(WORD_BREAK)
      .map(readWord)
      .filter((word) => word !== "")
      .join(" ")
  );
  return {
    output: lines.join("\n").trim(),
    invalid: [...result.invalid],
    unpaired: result.unpaired,
  };
}
